/**
 * The AI-AI conversation, in the slot the voices use on a human-human replay.
 *
 * Each chef is marked with the same drawing the kitchen and the brigade rail
 * use — colour, build, apron and hat — and named Red or Blue, so the line and
 * the chef on the canvas are visibly the same person and meaning never rests
 * on colour alone.
 */

import { useEffect, useRef } from 'react'
import type { SessionLogHeader } from '@overcooked/shared'
import { IconCanvas } from '../render/IconCanvas'
import { drawChefBadge } from '../render/players'
import { seatIdentity } from '../render/theme'
import type { ConversationLine } from './conversation'
import { stamp } from './utterances'

function ChefMark({ seat, size }: { seat: number; size: number }) {
  const identity = seatIdentity(seat)
  return (
    <IconCanvas
      className="chef-mark"
      size={size}
      signature={`chef-${seat}`}
      title={`${identity.label} chef (${identity.silhouette})`}
      draw={(ctx, s) => drawChefBadge(ctx, identity, s)}
    />
  )
}

function ChefName({ seat, suffix = '' }: { seat: number; suffix?: string }) {
  const identity = seatIdentity(seat)
  return (
    <span className="chef-name" style={{ color: identity.colour }}>
      {identity.label}
      {suffix}
    </span>
  )
}

export function Conversation({
  lines,
  header,
}: {
  /** Already cut at the playhead. */
  lines: ConversationLine[]
  header: SessionLogHeader | null
}) {
  const listRef = useRef<HTMLOListElement>(null)
  const newest = lines.length > 0 ? `${lines.length}-${lines[lines.length - 1].tick}` : ''

  // Pinned to the newest line, like any conversation, whenever one arrives.
  useEffect(() => {
    const list = listRef.current
    if (list) list.scrollTop = list.scrollHeight
  }, [newest])

  const seats = header?.participants?.length
    ? [...header.participants].sort((a, b) => a.seat - b.seat)
    : (header?.players ?? []).map((p) => ({ seat: p.seat, model: null as string | null }))

  return (
    <div className="replay-conversation">
      <div className="conversation-legend">
        {seats.map((p) => (
          <span className="conversation-chef" key={p.seat}>
            <ChefMark seat={p.seat} size={24} />
            <ChefName seat={p.seat} suffix=" chef" />
            {p.model && <span className="mono hint">{p.model}</span>}
          </span>
        ))}
        <span className="hint">What each chef said and did so far</span>
      </div>
      <ol className="conversation-lines" ref={listRef}>
        {lines.length === 0 && <li className="hint conversation-empty">Nothing yet.</li>}
        {lines.map((line, i) => (
          <li className="conversation-line" key={`${line.tick}-${line.seat}-${i}`}>
            <span className="mono conversation-stamp">{stamp(line.tick)}</span>
            <span className="conversation-who">
              <ChefMark seat={line.seat} size={20} />
              <ChefName seat={line.seat} />
            </span>
            <span className="conversation-body">
              {line.said && <span className="conversation-said">{line.said}</span>}
              {line.doing && (
                <span className="conversation-doing">
                  → <em>{line.doing}</em>
                  {line.why && <span className="conversation-why"> · “{line.why}”</span>}
                </span>
              )}
            </span>
          </li>
        ))}
      </ol>
    </div>
  )
}
